import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/AuthContext";
import { useAuth } from "@/AuthContext";
import { useI18n } from "@/i18n";
import { toast } from "sonner";
import { ShareBar } from "@/components/ShareBar";

export default function RecipeDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [r, setR] = useState(null);
  const [buying, setBuying] = useState(false);

  useEffect(() => {
    axios.get(`${API}/recipes/${id}`).then(res => setR(res.data)).catch(() => nav("/recipes"));
  }, [id, nav]);

  const buy = async () => {
    if (!user) {
      try {
        window.localStorage.setItem(
          "pending_purchase",
          JSON.stringify({ path: `/recipes/${id}`, action: "buy", at: Date.now() })
        );
      } catch (_) {}
      toast.message("Sign in to acquire this recipe");
      nav("/login");
      return;
    }
    try {
      setBuying(true);
      const origin = window.location.origin;
      const res = await axios.post(`${API}/checkout/session`, { recipe_id: id, origin_url: origin });
      window.location.href = res.data.url;
    } catch (e) {
      setBuying(false);
      toast.error(e?.response?.data?.detail || "Checkout failed");
    }
  };

  // Auto-resume the purchase if the visitor was redirected here after login
  useEffect(() => {
    if (!r || !user) return;
    try {
      const raw = window.localStorage.getItem("pending_purchase");
      if (!raw) return;
      let parsed;
      try {
        parsed = JSON.parse(raw);
      } catch (_) {
        // Corrupt entry — clean it up and stop.
        window.localStorage.removeItem("pending_purchase");
        return;
      }
      // Expire intents older than 30 minutes.
      const ageMs = Date.now() - (parsed?.at || 0);
      if (ageMs > 30 * 60 * 1000) {
        window.localStorage.removeItem("pending_purchase");
        return;
      }
      if (parsed?.path === `/recipes/${id}` && parsed?.action === "buy") {
        window.localStorage.removeItem("pending_purchase");
        // Never re-charge a recipe the user already owns.
        if (r.owned) return;
        buy();
      }
    } catch (_) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [r, user, id]);

  if (!r) return <div className="max-w-7xl mx-auto p-10 text-zinc-500" data-testid="recipe-loading">Loading…</div>;
  const tr = (r[lang] && r[lang].title) ? r[lang] : r.en;
  const img = r.image_b64 || r.image_url;
  return (
    <main className="max-w-7xl mx-auto px-6 lg:px-10 py-12" data-testid={`recipe-detail-${r.id}`}>
      <Link to="/recipes" className="overline" data-testid="back-to-recipes">← {t("nav_portfolio")}</Link>
      <div className="grid lg:grid-cols-12 gap-10 mt-8">
        <div className="lg:col-span-7">
          <img
            src={img}
            alt={tr.title}
            className="w-full h-[640px] object-cover"
            data-testid="recipe-image"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1200&q=80";
            }}
          />
        </div>
        <div className="lg:col-span-5">
          <div className="overline">{r.capital_city} · {r.capital_country}</div>
          <h1 className="font-serif text-5xl tracking-tighter mt-4">{tr.title}</h1>
          <p className="text-zinc-400 italic font-light text-lg mt-4">{tr.tagline}</p>
          <p className="text-zinc-300 leading-relaxed mt-6">{tr.story}</p>
          <div className="text-zinc-500 text-sm mt-6">{t("by")} <span className="gold">{r.restaurant || "—"}</span> {t("in_")} {r.capital_city}.</div>
          <div className="divider-gold my-8" />
          <div className="flex items-end gap-4">
            <div className="font-serif text-5xl gold" data-testid="recipe-price">£{r.price}</div>
            <div className="overline pb-2">{r.type === "meal" ? "per meal recipe" : "per dessert recipe"}</div>
          </div>
          {r.owned ? (
            <div className="mt-6 tag tag-gold" data-testid="owned-badge">{t("cta_owned")} ✓</div>
          ) : (
            <button disabled={buying} onClick={buy} className="btn-gold mt-6 w-full justify-center" data-testid="buy-button">
              {buying ? "…" : t("cta_unlock")}
            </button>
          )}
          {!r.owned && <p className="text-xs text-zinc-500 mt-4" data-testid="locked-note">🔒 {t("locked_note")}</p>}
          <ShareBar
            url={typeof window !== "undefined" ? window.location.href : ""}
            title={`${tr.title} — ${r.capital_city}`}
            image={img}
            label={t("share_recipe")}
          />
        </div>
      </div>

      {r.owned && (
        <section className="mt-16 grid lg:grid-cols-2 gap-10" data-testid="recipe-unlocked-content">
          <div>
            <div className="overline">{t("ingredients")}</div>
            <h3 className="font-serif text-3xl mt-2">{t("ingredients")}</h3>
            <ul className="mt-6 space-y-3 text-zinc-300">
              {tr.ingredients?.map((i,idx) => (
                <li key={idx} className="border-b border-white/5 pb-3 flex gap-3">
                  <span className="gold font-serif">{String(idx+1).padStart(2,"0")}</span>
                  <span>{i}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="overline">{t("method")}</div>
            <h3 className="font-serif text-3xl mt-2">{t("method")}</h3>
            <ol className="mt-6 space-y-5 text-zinc-300">
              {tr.steps?.map((s,idx) => (
                <li key={idx} className="border-l-2 border-[#D4AF37]/40 pl-5">
                  <div className="overline mb-2">Step {idx+1}</div>
                  <div className="leading-relaxed">{s}</div>
                </li>
              ))}
            </ol>
          </div>
        </section>
      )}
    </main>
  );
}
